import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, ArrowLeft, BookOpen } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { StructuredData } from '../components/StructuredData';
import { blogStructuredData } from '../seo/structuredData';

export const BlogPage = () => {
  useEffect(() => {
    // SEO: page title + description
    const prevTitle = document.title;
    document.title =
      'Vargas Tire Auto Repair Blog — Mobile Mechanic & Car Care Tips Jacksonville';

    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      return el;
    };

    const descMeta = setMeta(
      'description',
      'Vargas Tire Auto Repair blog — daily car care tips, mobile mechanic advice, mobile oil change Jacksonville, roadside assistance Jacksonville, tire service Jacksonville, brake service Jacksonville, battery jump and lockout service.'
    );
    const kwMeta = setMeta(
      'keywords',
      'mobile mechanic Jacksonville, mobile oil change Jacksonville, roadside assistance Jacksonville, tire service Jacksonville, brake service Jacksonville, battery jump Jacksonville, lockout service Jacksonville, car care tips, Vargas Tire Auto Repair, Vargas Car Care'
    );

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    const prevHref = canonical?.getAttribute('href');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://vargastireautorepair.com/blog');

    // Inject Soro embed script (defer) — only once per page life
    const SCRIPT_ID = 'soro-blog-embed-script';
    const SCRIPT_SRC =
      'https://app.trysoro.com/api/embed/e7c19f83-2932-4a42-9231-df3d05a80a56';

    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.defer = true;
      document.body.appendChild(script);
    }

    return () => {
      document.title = prevTitle;
      // Reset descriptions for other pages
      if (descMeta) descMeta.setAttribute('content', '');
      if (kwMeta) kwMeta.setAttribute('content', '');
    };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <StructuredData id="ld-blog" data={blogStructuredData} />
      <Header />

      {/* Hero band */}
      <section className="relative pt-28 pb-12 sm:pt-32 sm:pb-16 bg-gradient-to-b from-black via-zinc-900 to-black border-b border-yellow-500/20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-yellow-400 hover:text-yellow-300 text-sm mb-6"
            data-testid="blog-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-red-600">
              <BookOpen className="w-6 h-6 text-white" />
            </span>
            <span className="text-yellow-400 uppercase tracking-widest text-xs font-semibold">
              Vargas Car Care
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">
            Vargas Tire Auto Repair{' '}
            <span className="text-red-500">Blog</span>
          </h1>
          <p className="mt-4 text-base sm:text-lg text-gray-300 max-w-3xl">
            Daily car care tips, mobile mechanic advice, and roadside know-how
            for Jacksonville drivers — oil changes, tires, brakes, batteries,
            jump starts, lockouts and more.
          </p>
        </div>
      </section>

      {/* Soro Blog Embed */}
      <section className="flex-1 py-12 sm:py-16 bg-zinc-950">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white text-gray-900 rounded-2xl shadow-2xl ring-1 ring-yellow-500/30 p-4 sm:p-8 lg:p-10 min-h-[60vh]">
            {/* Soro embed — DO NOT modify the script src or remove defer */}
            <div id="soro-blog" data-testid="soro-blog-embed"></div>
            <noscript>
              <p className="text-center text-gray-600 py-12">
                Please enable JavaScript to view the latest articles from
                Vargas Car Care.
              </p>
            </noscript>
          </div>

          <p className="text-center text-xs text-gray-500 mt-6">
            New articles posted regularly. If the blog doesn’t load, please
            refresh the page.
          </p>
        </div>
      </section>

      {/* Footer contact band */}
      <section className="bg-black border-t border-yellow-500/20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            <div>
              <h3 className="text-yellow-400 font-bold text-lg mb-2">
                Vargas Tire Auto Repair
              </h3>
              <p className="text-gray-400 text-sm">
                Mobile mechanic & tire service serving North Florida.
              </p>
            </div>
            <a
              href="tel:3057724150"
              className="flex items-center gap-3 text-gray-200 hover:text-red-400 transition-colors"
              data-testid="blog-footer-phone"
            >
              <span className="w-10 h-10 rounded-lg bg-red-600/20 flex items-center justify-center">
                <Phone className="w-5 h-5 text-red-500" />
              </span>
              <span>
                <span className="block text-xs uppercase text-gray-500">
                  Phone
                </span>
                <span className="text-lg font-semibold phone-number">
                  305-772-4150
                </span>
              </span>
            </a>
            <div className="flex items-center gap-3 text-gray-200">
              <span className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-yellow-400" />
              </span>
              <span>
                <span className="block text-xs uppercase text-gray-500">
                  Service Area
                </span>
                <span className="text-lg font-semibold">
                  Jacksonville, Florida
                </span>
              </span>
            </div>
          </div>

          <div className="mt-8 flex justify-center">
            <a href="tel:3057724150">
              <Button
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-6 text-base"
                data-testid="blog-footer-call-btn"
              >
                <Phone className="w-4 h-4 mr-2" />
                Call (305) 772-4150
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
